import { PublicClientApplication, InteractionType, InteractionRequiredAuthError, BrowserAuthError, BrowserAuthErrorCodes } from "@azure/msal-browser";
import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";
import { Client } from "@microsoft/microsoft-graph-client";

import { getFSmessage } from './messages.js';
const msalConfig = {
  auth: {
    clientId: "b230cab3-e284-4486-a990-365fff58996d",
    redirectUri: location.origin, // This should match the redirect URI registered in Azure AD
  },
};

var client = null;


// firmId and userId are used to check whether a particular email has already been processed and meta data stored in firestore
var firmId = null;
var userId = null;


const msalInstance = new PublicClientApplication(msalConfig);

async function initializeApi(settings, firm_id=null, user_id = null) {
  firmId = firm_id;
  userId = user_id;
  await msalInstance.initialize();
};

async function authenticate(account=null) {
  //TODO suggest email here/?
  var loginRequest = {
    scopes: ["mail.read", "user.read"], // optional Array<string>
  };

  try {
    var authResult = null;
    if (account) {
      loginRequest.account = account;
      authResult = await msalInstance.acquireTokenSilent(loginRequest).then(tokenResponse => {
        return tokenResponse;
      }).catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return msalInstance.acquireTokenPopup(loginRequest);
        }
        const iframeErrors = [
          BrowserAuthErrorCodes.monitorWindowTimeout,
          BrowserAuthErrorCodes.emptyWindowError,
          BrowserAuthErrorCodes.blockIframeReload,
          BrowserAuthErrorCodes.iframeClosedPrematurely,
        ];
        if (error instanceof BrowserAuthError && iframeErrors.includes(error.errorCode)) {
          // Silent renewal uses a hidden iframe pointed at redirectUri. When the
          // server responds with X-Frame-Options: DENY (as dev.patensys.com does)
          // the iframe never loads and the call times out. Popups are not subject
          // to X-Frame-Options, so retry interactively.
          return msalInstance.acquireTokenPopup(loginRequest);
        }
        throw error;
      });
    } else {
      authResult = await msalInstance.acquireTokenPopup(loginRequest);
    }

    if (!authResult.account) {
      throw new Error('Could not authenticate');
    }
    const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(msalInstance, {
      account: authResult.account,
      interactionType: InteractionType.Popup,
      scopes: ["mail.read", "user.read"],
    });

    client = Client.initWithMiddleware({ authProvider: authProvider });
    return authResult;

  } catch (err) {
    console.error('Error logging in:', err);
    throw err; // Propagate the error to the caller
  }
}

function signout() {
  return false;
}

function alreadySignedIn() {
  return client;
}


async function getProfileEmailAddress() {
  const response = await client.api('/me').get();
  if (response) {
    return response.mail;
  } else {
    return null;
  }
}

async function getEmails(filter, maxResults=100, pagetoken=null) {

  let request = client.api('/me/messages').top(maxResults);
  if (pagetoken) {
    request = request.skip(pagetoken * maxResults);
  } else {
    pagetoken = 0;
  }
  const response = await request.get();
  const messages = response.value
  let filteredMessages = [];
  for (const message of messages) {
    message.threadId = message.conversationId;
    if (!await getFSmessage(firmId, userId, message.id)) {
      filteredMessages.push(message);
    }
  }
  //TODO maybe need to add email content here...
  return {query: filter, nextPageToken: pagetoken + 1, messages: filteredMessages};
}

async function loadBatch(lastBatch, query='') {
  let token = lastBatch ? lastBatch.nextPageToken : null;
  return await getEmails(query, 25, token);
}


function getBody(email) {
  //this might need to be platform specific -> move to service
  if (!email.body) {
    return '';
  }
  return extractOutlookBody(email.body);
}

function extractOutlookBody(body) {
  let result = {
    html: null,
    plain: null,
  };

  if (!body || !body.contentType || !body.content) {
    return "";
  }

  if (body.contentType === 'html') {
    result.html = body.content;
  } else if (body.contentType === 'text') {
    result.plain = body.content.replace(/\n/g, '<br>');
  }
  
  return result.html || result.plain || '';
}

async function getThread(threadId) {
  if (!threadId) {
    return null;
  }

  try {
    const messages = await client.api('/me/messages').top(1000).filter(`conversationId eq '${threadId}'`).get();
    return {messages: messages.value, id: threadId};

  } catch (error) {
    console.error('Error in getThread request:', error);
    // if error is 404, then the thread is not found
    if (error.result && error.result.error && error.result.error.code === 404) {
      console.log('Thread not found:', threadId);
      throw new Error('Thread not found');
    }
  }
}

function getThreadSender(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0)) {
    return "";
  }
  if (!thread.messages[thread.messages.length -1].from ||
      !thread.messages[thread.messages.length -1].from.emailAddress) {
    return "";
  }
  let emailAddress = thread.messages[thread.messages.length -1].from.emailAddress;
  return emailAddress?.name + " <" + emailAddress?.address + ">";
}

//function getThreadDate(thread) {
function getThreadDate(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0)) {
    return "";
  }
  return thread.messages[thread.messages.length -1].receivedDateTime;
}


function getThreadSubject(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0)) {
    return "";
  }
  return thread.messages[thread.messages.length -1].subject;
}

function getThreadSnippet(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0)) {
    return "";
  }
  return thread.messages[thread.messages.length -1].bodyPreview;
}

function getSubjectLine(email) {
  if (!email) {
    return '';
  }
  return email.subject;
}

function platformDate(email) {
  if (!email) {
    return null;
  }
  return new Date(email.receivedDateTime).valueOf();
}

function moreMessages(batch) {
  return (batch.messages.length > 0  && batch.nextPageToken);
}

async function getEmail(messageId) {
  if (!messageId) {
    return null;
  }
  const message = await client.api(`/me/messages/${messageId}`).get();
  message.threadId = message.conversationId;
  return message.value;
}

function getHeaders(email) {
  //Kinda wierd, just need from and subject for email single
  const emailaddr = email &&
                    email.from &&
                    email.from.emailAddress &&
                    email.from.emailAddress?.name + " <" + email.from.emailAddress?.address + ">";
  let headers = {"Subject": email.subject,
                 "From": emailaddr };
  return headers;
}


export {
  initializeApi,
  authenticate,
  signout,
  alreadySignedIn,
  getProfileEmailAddress,
  getEmails,
  getThread,
  getThreadSender,
  getThreadDate,
  getThreadSubject,
  getThreadSnippet,
  getBody,
  getEmail,
  loadBatch,
  getSubjectLine,
  platformDate,
  moreMessages,
  getHeaders,
}
